import React, { FunctionComponent } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { RenderType } from "../../../../Enums/RenderType";

import {
    IGPSBenchmarkCommonData,
    IPolygon,
    IRootReducer,
} from "../../../../Models/Models";
import { gpsVisitorBehaviorsContainerSelector } from "../../../../Reducers/Selectors/GPSInsightsSelectors";
import {
    AnimatedReportCard,
    ReportH1,
    ReportScrollableSection,
    ReportSectionHeader,
    ReportSectionNumber,
} from "../../../Common";
import { FrequentlyVisitedPlaces } from "./FrequentlyVisitedPlaces";
import { VisitorLoyalty } from "./VisitorLoyalty";

interface VisitorBehaviorsContainerProps {
    location1Name: string;
    location2Name?: string;
    benchmarkCommonData?: IGPSBenchmarkCommonData;
    renderType?: RenderType;
    app?: DigitalShareAppType;
    onMapReady?: () => void;
}

const VisitorBehaviorsContainer: FunctionComponent<VisitorBehaviorsContainerProps>
  = (props: VisitorBehaviorsContainerProps) => {
      /** misc */
      const { location2Name } = props;
      /** store */
      const { fvpData, showVisitorBehaviors, polygons } = useSelector(
          gpsVisitorBehaviorsContainerSelector
      );

      const polygonsAreIdentical = () => {
          if (!polygons || polygons?.length === 0) {
              return false;
          }

          const polyLengths = polygons.map(
              (polygon) => polygon.linearRings[0].length
          );
          const allSameLength: boolean = polyLengths.every(
              (length) => (length = polyLengths[0])
          );

          if (!allSameLength) {
              return false;
          }

          const comparisonPolygon: IPolygon = polygons[0];
          const polyLength: number = polyLengths[0];

          for (let i = 0; i < polyLength; i++) {
              const comparisonPoint = comparisonPolygon.linearRings[0][i];

              const identicalX = polygons.every((polygon) => {
                  const pointInThisPoly = polygon.linearRings[0][i];
                  return pointInThisPoly.x === comparisonPoint.x;
              });
              const identicalY = polygons.every((polygon) => {
                  const pointInThisPoly = polygon.linearRings[0][i];
                  return pointInThisPoly.y === comparisonPoint.y;
              });

              if (!(identicalX && identicalY)) {
                  return false;
              }
          }

          return true;
      };

      return showVisitorBehaviors ? (
          <ReportScrollableSection id="GPSVisBeh">
              <ReportSectionHeader>
                  <ReportSectionNumber>04</ReportSectionNumber>
                  <ReportH1>Visitor Behaviors</ReportH1>
              </ReportSectionHeader>
              <AnimatedReportCard
                  scrollableParentSelector="#GPSReportBody"
                  animateIn="zoomIn"
                  offset={100}
                  animateOnce={true}
              >
                  <FrequentlyVisitedPlaces
                      fvpData={fvpData}
                      app={props.app}
                      itemType={DigitalShareItemType.FrequentlyVisitedPlaces}
                      renderType={props.renderType}
                      onMapReady={props.onMapReady}
                  />

                  {location2Name && !polygonsAreIdentical() && (
                      <VisitorLoyalty
                          {...props}
                          app={props.app}
                          itemType={DigitalShareItemType.VisitorLoyalty}
                          renderType={props.renderType}
                          scrollableParentSelector="#GPSReportBody"
                      />
                  )}
              </AnimatedReportCard>
          </ReportScrollableSection>
      ) : null;
  };

export default VisitorBehaviorsContainer;
